import React, { forwardRef, ReactElement, useImperativeHandle } from 'react';
import { Controller, FieldValues, useForm } from 'react-hook-form';
import { LoadingButton } from '@mui/lab';
import { Box, Button, FormHelperText, Typography } from '@mui/material';
import { has, isArray, isEmpty, isNull, isString } from 'lodash';

import { formField } from './formField';
import { FormRendererProps, RefProps, Schema } from './props';

const FormRendererComponentView = <T extends FieldValues>(
  props: FormRendererProps<T>,
  ref: React.ForwardedRef<RefProps<T>>
) => {
  //========= PROPS DESTRUCTURING
  const {
    schema,
    enableRef = false,
    form: customForm,
    footer,
    onSubmit,
    textProps,
    formOptions,
    footerProps,
    controllerProps,
    parentContainerProps,
    fieldContainerProps,
    enableFooter = true,
    ...rest
  } = props;
  //========= HOOKS
  const form = useForm<any, T>(formOptions);

  //========= VARIABLES
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = customForm ?? form;

  // pass watch down
  const schemaRender = enableRef
    ? (schema as ({ ref, watch, setValue, reset }: any) => Schema[])({
        ref,
        watch,
        setValue,
        reset,
      })
    : schema;

  //========= REF
  useImperativeHandle<RefProps<T>, any>(ref, () => {
    return {
      form,
      submit: handleSubmit(onSubmit),
    };
  });

  //========= VIEW
  const renderFormComponent = (field: Schema) => {
    let isError = false;
    let errorMessage = '';

    // find the component by using the name
    const Comp: any = formField?.find((x) => x.name === field.key)?.comp;
    if (errors && has(errors, field?.name)) {
      isError = true;
      errorMessage = (errors?.[field?.name] as any)?.message;
    }

    if (field?.type === 'custom' && field?.render) {
      return (
        <>
          <Controller
            {...controllerProps}
            name={field?.name}
            render={(fieldState) => field.render?.(fieldState)}
            control={control}
          />
          {isError && <FormHelperText error>{errorMessage}</FormHelperText>}
        </>
      );
    }

    if (!Comp) {
      console.warn(`${field?.key} is not registered.`);
      return null;
    }

    if (field?.type === 'component') {
      return <Comp {...field.props} />;
    }
    return (
      <Controller
        {...controllerProps}
        name={field?.name}
        defaultValue={field?.defaultValue}
        render={({ field: { onChange, onBlur, value, ...restFieldProps } }) => {
          // a callback that allow user to access the value
          field?.dataMap?.({ onChange, value, onBlur });
          return (
            <>
              {field?.renderPreComponent?.()}
              <Comp
                // can add more if there's anything want to pass down to the component
                {...field?.formStateMap?.(isError, value)}
                value={value}
                error={isError}
                onChange={
                  field?.customOnChange
                    ? (...args: any) => field?.customOnChange?.(args, onChange)
                    : onChange
                }
                onBlur={onBlur}
                {...field?.props}
                {...restFieldProps}
              />
              {isError && <FormHelperText error>{errorMessage}</FormHelperText>}
              {field?.renderPostComponent?.()}
            </>
          );
        }}
        control={control}
      />
    );
  };

  const renderField = () => {
    return (
      <Box
        display="grid"
        className="grid-cols-6 md:grid-cols-12"
        gap={7}
        {...fieldContainerProps}
      >
        {((isArray(schemaRender) && schemaRender) || []).map(
          (field: Schema, i: number) => {
            // if field is a string, return text only
            if (isString(field))
              return (
                <Typography className="py-2" variant="h6" {...textProps}>
                  {field}
                </Typography>
              );

            if (isNull(field)) return field;

            return (
              <Box
                key={i}
                gridColumn={{
                  xs: `span ${field?.mobileColSpan ?? 6}`,
                  md: `span ${field?.colSpan ?? 12}`,
                }}
                {...field?.containerProps}
              >
                {renderFormComponent(field)}
              </Box>
            );
          }
        )}
      </Box>
    );
  };

  const renderFooter = () => {
    const {
      buttonContainerProps,
      isLoading = false,
      disabled = false,
      primaryText = 'Save',
      hasSecondaryButton = true,
      secondaryButtonProps,
      secondaryText = 'Cancel',
      additionalButton,
      additionalCondition = false,
      validateDisable = false,
    } = footerProps || {};

    const disabledCondition = () => {
      if (validateDisable) {
        const watchFunc = watch();

        return Object.entries(watchFunc).some(([key, value]) => {
          if (!value) return true;
          if (additionalCondition) return additionalCondition;
          if (Array.isArray(value) && isEmpty(value)) return true;
          return false;
        });
      }
      return disabled;
    };
    return (
      <Box
        display={'flex'}
        justifyContent="flex-end"
        pt={12}
        gap={5}
        className="md:flex-row md:pl-0 flex-col-reverse w-full pt-8 pb-4 gap-4 [&>button]:rounded-lg [&>button]:py-2"
        {...buttonContainerProps}
      >
        {additionalButton}
        {hasSecondaryButton ? (
          <Button
            variant="outlined"
            disabled={disabled}
            {...secondaryButtonProps}
          >
            {secondaryText}
          </Button>
        ) : null}
        <LoadingButton
          variant="contained"
          type="submit"
          disabled={disabledCondition()}
          loading={isLoading}
          // {...primaryButtonProps}
        >
          {primaryText}
        </LoadingButton>
      </Box>
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} ref={ref} {...rest}>
      <Box py={2} {...parentContainerProps}>
        {renderField()}
        {enableFooter && (footer ?? renderFooter())}
      </Box>
    </form>
  );
};

const FormRendererView = forwardRef(FormRendererComponentView) as <
  T extends FieldValues = FieldValues
>(
  props: FormRendererProps<T> & { ref?: React.ForwardedRef<RefProps<T>> }
) => ReactElement;

export type SchemaProps = Schema[];
export type FormRefProps = RefProps;
export const FormRenderer = FormRendererView;
export default FormRendererView;
